import React, { useState, useEffect } from 'react';
import { Row, Col, Table, Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import classNames from 'classnames';

import { useReturnManifestDetailContext } from 'providers/ReturnManifestDetailProvider';

import { returnManifestApi } from 'api';

const ReturnManifestShipmentExpandedRow = ({ shipment }) => {
  const [lines, setLines] = useState([]);
  const [returnComments, setReturnComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const { startEditing, finishEditing } = useReturnManifestDetailContext();

  useEffect(() => {
    if (shipment) {
      const initialLines = shipment?.invoice_status_shipment_lines.map(
        line => ({
          id: line.id,
          return_inventory_type: line.return_inventory_type || 'a',
        }),
      );

      setLines(initialLines);
      setReturnComments(shipment.return_comments || '');
      setHasLocalChanges(false);
    }
  }, [shipment]);

  const handleInventoryTypeChange = (lineId, newType) => {
    setLines(prevLines =>
      prevLines.map(line =>
        line.id === lineId ? { ...line, return_inventory_type: newType } : line,
      ),
    );

    setHasLocalChanges(true);
    if (!hasLocalChanges) {
      startEditing(shipment.id);
    }
  };

  const handleReturnCommentsChange = value => {
    setReturnComments(value);

    setHasLocalChanges(true);
    if (!hasLocalChanges) {
      startEditing(shipment.id);
    }
  };

  const handleUpdateSave = async () => {
    if (!shipment) return;

    setIsSubmitting(true);

    try {
      // null 일 경우, 'a'로 요청
      const processedLines = lines.map(line => ({
        ...line,
        return_inventory_type: line.return_inventory_type || 'a',
      }));

      const data = {
        lines: processedLines,
        return_comments: returnComments,
      };

      await returnManifestApi.updateReturnManifestShipment(shipment.id, data);

      toast.success('Successfully updated return manifest shipment');

      setHasLocalChanges(false);
      finishEditing(shipment.id);
    } catch (error) {
      toast.error('Failed to update return manifest shipment');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!shipment) return null;

  return (
    <div
      className={classNames('p-2', {
        'border-bottom': !hasLocalChanges,
        'border border-danger border-2 rounded shadow-sm': hasLocalChanges,
      })}
    >
      <Row>
        <Col md={8}>
          {shipment?.invoice_status_shipment_lines?.length > 0 && (
            <div className="table-responsive border">
              <Table size="sm" bordered className="mb-0">
                <colgroup>
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '50%' }} />
                  <col style={{ width: '25%' }} />
                </colgroup>

                <thead className="bg-200">
                  <tr>
                    <th className="text-nowrap text-center fs-11">#</th>
                    <th className="text-nowrap text-start fs-11">SKU</th>
                    <th className="text-nowrap text-start fs-11">
                      Product Name
                    </th>
                    <th className="text-nowrap text-center fs-11">
                      Inventory Category
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {shipment?.invoice_status_shipment_lines?.map(
                    (shipment_line, idx) => (
                      <tr key={shipment_line.id}>
                        <td className="align-middle text-center fs-11">
                          {idx + 1}
                        </td>
                        <td className="align-middle text-start fs-11">
                          {shipment_line?.product.sku || '—'}
                        </td>
                        <td className="align-middle text-start fs-11">
                          {shipment_line?.product.name || '—'}
                        </td>
                        <td className="text-center">
                          <Form.Select
                            size="sm"
                            value={
                              lines.find(line => line.id === shipment_line.id)
                                ?.return_inventory_type || 'a'
                            }
                            onChange={e =>
                              handleInventoryTypeChange(
                                shipment_line.id,
                                e.target.value,
                              )
                            }
                          >
                            <option value="a">A</option>
                            <option value="b">B</option>
                            <option value="c">C</option>
                            <option value="missing">Missing</option>
                          </Form.Select>
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Col>

        <Col md={4}>
          <div className="d-flex flex-column gap-2 ">
            <Form.Control
              className="fs-10"
              as="textarea"
              rows={1}
              value={returnComments}
              onChange={e => handleReturnCommentsChange(e.target.value)}
              placeholder="Enter return reason..."
            />

            {hasLocalChanges && (
              <div className="d-flex justify-content-end align-items-center gap-2">
                <span className="text-danger fs-11">*Need to be saved</span>

                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleUpdateSave}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default ReturnManifestShipmentExpandedRow;
